import memoize from 'lodash.memoize';
import { Filters, SearchMode } from 'model/Filters';
import { createFilter, getSubOption } from 'data/filters';
import cloneDeep from 'lodash.clonedeep';

function filterFactory(filter: Filters) {
  const newFilters: Filters = cloneDeep(filter);
  newFilters.country = null;
  newFilters.city = null;
  newFilters.activeFilters = newFilters.activeFilters.filter(({ uid }) => ['date.from', 'date.to', 'adults', 'children'].includes(uid));
  const commands = {
    setCity(city) {
      newFilters.city = city;
      return commands;
    },
    setCountry(country) {
      newFilters.country = country;
      return commands;
    },
    addKeyword(keyword) {
      const keywordFilter = createFilter('keywords');
      keywordFilter.value = keyword;
      newFilters.activeFilters.push(keywordFilter);
      return commands;
    },
    addTag(tag) {
      const tagFilter = createFilter('tags.items[]');
      const tagOption = getSubOption('tags.items[]', tag);
      if (!tagOption) {
        return commands.addKeyword(tag);
      }
      tagFilter.subOptions = [tagOption];
      newFilters.activeFilters.push(tagFilter);
      return commands;
    },
    create() {
      return newFilters;
    },
  };
  return commands;
}

type KeywordDescriptor = {
  country?: string;
  city?: string;
  tag?: string;
  keyword?: string;
};

const keywordsMap: Record<string, KeywordDescriptor> = {
  Pompeii: { country: 'italy', city: 'naples', tag: 'private-pompei-day-trips' },
  Tuscany: { keyword: 'Tuscany' },
  Pattaya: { keyword: 'Pattaya' },
  Toledo: { keyword: 'Toledo' },
  Colosseum: { country: 'italy', city: 'rome', tag: 'private-colosseum-tours' },
  'Mekong Delta': { keyword: 'Mekong Delta' },
  Saigon: { keyword: 'Saigon' },
  Andorra: { keyword: 'Andorra' },
  Delphi: { keyword: 'Delphi' },
  Borobudur: { keyword: 'Borobudur' },
  Douro: { keyword: 'Douro' },
  Vatican: { keyword: 'Vatican' },
  Murano: { keyword: 'Murano' },
  Ayutthaya: { keyword: 'Ayutthaya' },
  Evora: { country: 'portugal', city: 'lisbon', tag: 'evora' },
  Kanchanaburi: { keyword: 'Kanchanaburi' },
  Normandy: { keyword: 'Normandy' },
  Haarlem: { keyword: 'Haarlem' },
  'Sagrada familia': { keyword: 'Sagrada familia' },
  Frascati: { keyword: 'Frascati' },
  Giverny: { keyword: 'Giverny' },
  Louvre: { country: 'france', city: 'paris', tag: 'louvre-private-tours' },
  Versailles: { keyword: 'Versailles' },
  Delft: { keyword: 'Delft' },
  Herculaneum: { keyword: 'Herculaneum' },
  Volendam: { keyword: 'Volendam' },
  Acropolis: { keyword: 'Acropolis' },
  Essaouira: { keyword: 'Essaouira' },
  Montserrat: { country: 'spain', city: 'barcelona', tag: 'montserrat-day-trips' },
  Sintra: { country: 'portugal', city: 'lisbon', tag: 'sintra-day-trips' },
  'Amalfi coast': { country: 'italy', city: 'naples', tag: 'amalfi-coast-day-trips' },
  'Batu Caves': { keyword: 'Batu Caves' },
  'Cameron highlands': { keyword: 'Cameron highlands' },
  Cappadocia: { keyword: 'Cappadocia' },
  Montmartre: { keyword: 'Montmartre' },
  'Guimarães': { keyword: 'Guimarães' },
  Aveiro: { keyword: 'Aveiro' },
  Potsdam: { keyword: 'Potsdam' },
  Ouarzazate: { keyword: 'Ouarzazate' },
  Peloponnese: { keyword: 'Peloponnese' },
  Carmona: { keyword: 'Carmona' },
  Pilsen: { keyword: 'Pilsen' },
  Tarragona: { keyword: 'Tarragona' },
  Valldemossa: { keyword: 'Valldemossa' },
  Zalipie: { keyword: 'Zalipie' },
  'Fátima': { keyword: 'Fátima' },
  Kitulgala: { keyword: 'Kitulgala' },
  Modena: { keyword: 'Modena' },
  Pistoia: { keyword: 'Pistoia' },
  Zakopane: { keyword: 'Zakopane' },
};

export const keywords: string[] = Object.keys(keywordsMap);

export const filtersToKeyword = (filters) => {
  const filter = filters.activeFilters.find(({ uid }) => ['keywords', 'tags.items[]'].includes(uid));
  const pattern = filter && (filter.value || filter?.subOptions?.[0]?.value);
  if (pattern) {
    return Object.keys(keywordsMap)
      .find((key) => {
        const { tag, keyword } = keywordsMap[key];
        return [tag, keyword].includes(pattern);
      });
  }
  return null;
};

export const keywordToFilters = memoize((word: string) => {
  const { city, country, keyword, tag } = keywordsMap[word];
  return (filters: Filters) => {
    const factory = filterFactory(filters);
    city && factory.setCity(city);
    country && factory.setCountry(country);
    keyword && factory.addKeyword(keyword);
    tag && factory.addTag(tag);

    return factory.create();
  };
});

export const getSearchMode = memoize((word: string) => !!keywordsMap[word].tag ? SearchMode.LANDING_PAGE : SearchMode.EXPERIENCES);

export const filtersToMode = (filters) => {
  const keyword = filtersToKeyword(filters);
  return keyword ? getSearchMode(keyword) : null;
};
